import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { organizationMembershipSettingsRoute } from "network/api-routes";
import { membershipCurrencySymbol } from "utils/membership-ui-helpers";
import OrganizationMembershipOverviewCards from "./OrganizationMembershipOverviewCards";
import OrganizationMembershipImportSection from "./OrganizationMembershipImportSection";
import OrganizationMembershipPaymentsList from "./OrganizationMembershipPaymentsList";

const EMPTY_FORM = {
  membership_enforcement: "soft",
  membership_grace_days: 7,
  require_membership_to_play: false,
  membership_billing_kind: "monthly",
  membership_amount: "",
  membership_split_into_12: false,
};

const normalizeAmount = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const amountsConfigured = (settings) => {
  const amount = normalizeAmount(settings?.membership_amount);
  return amount !== null && amount > 0;
};

const moneyFieldsChanged = (current, baseline) => {
  if (!baseline) return false;
  if ((current.membership_billing_kind || "monthly") !== (baseline.membership_billing_kind || "monthly")) {
    return true;
  }
  const currentSplit =
    current.membership_billing_kind === "yearly" ? Boolean(current.membership_split_into_12) : false;
  const baselineSplit =
    baseline.membership_billing_kind === "yearly" ? Boolean(baseline.membership_split_into_12) : false;
  if (currentSplit !== baselineSplit) return true;

  const currentAmount = normalizeAmount(current.membership_amount);
  const baselineAmount = normalizeAmount(baseline.membership_amount);
  if (currentAmount === null && baselineAmount === null) return false;
  if (currentAmount === null || baselineAmount === null) return true;
  return currentAmount !== baselineAmount;
};

const OrganizationMembershipSettingsPage = () => {
  const intl = useIntl();
  const { user, selectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currencyCode, setCurrencyCode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadedSettings, setLoadedSettings] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  // Bump these after save / import so overview + payments reload (Task 6 uses both).
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);

  const refreshOverview = useCallback(() => setOverviewRefreshKey((key) => key + 1), []);
  const refreshPayments = useCallback(() => setPaymentsRefreshKey((key) => key + 1), []);
  const refreshDashboard = useCallback(() => {
    refreshOverview();
    refreshPayments();
  }, [refreshOverview, refreshPayments]);

  const applySettingsResponse = (data = {}) => {
    setCurrencyCode(data.currency_code || null);
    const nextForm = {
      membership_enforcement: data.membership_enforcement || "soft",
      membership_grace_days: data.membership_grace_days ?? 7,
      require_membership_to_play: Boolean(data.require_membership_to_play),
      membership_billing_kind: data.membership_billing_kind || "monthly",
      membership_amount: data.membership_amount ?? "",
      membership_split_into_12: Boolean(data.membership_split_into_12),
    };
    setForm(nextForm);
    setLoadedSettings(nextForm);
  };

  const loadSettings = useCallback(() => {
    if (!selectedOrganization?.id) return;
    setLoading(true);
    performRequest("get", organizationMembershipSettingsRoute(selectedOrganization.id), {}, true, user)
      .then((res) => applySettingsResponse(res.data || {}))
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.settings.loadError" }),
          { variant: "error" },
        ),
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, intl, selectedOrganization, user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const buildPayload = (applyMode = null) => {
    const payload = {
      membership_enforcement: form.membership_enforcement,
      membership_grace_days: form.membership_grace_days,
      require_membership_to_play: form.require_membership_to_play,
      membership_billing_kind: form.membership_billing_kind,
      membership_amount: form.membership_amount === "" ? null : Number(form.membership_amount),
      membership_split_into_12:
        form.membership_billing_kind === "yearly" ? Boolean(form.membership_split_into_12) : false,
    };
    if (applyMode) payload.apply_mode = applyMode;
    return payload;
  };

  const saveSettings = (applyMode = null) => {
    setSaving(true);
    performRequest(
      "patch",
      organizationMembershipSettingsRoute(selectedOrganization.id),
      buildPayload(applyMode),
      true,
      user,
    )
      .then((res) => {
        const data = res.data || {};
        applySettingsResponse(data);
        setApplyDialogOpen(false);
        refreshOverview();
        enqueueSnackbar(intl.formatMessage({ id: "org.membership.settings.saveSuccess" }), { variant: "success" });
        const created = data.auto_enroll?.created;
        if (typeof created === "number" && created > 0) {
          enqueueSnackbar(intl.formatMessage({ id: "org.membership.autoEnroll" }, { count: created }), {
            variant: "info",
          });
        }
      })
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.settings.saveError" }),
          { variant: "error" },
        ),
      )
      .finally(() => setSaving(false));
  };

  const handleSave = () => {
    const needsApplyMode = moneyFieldsChanged(form, loadedSettings) && amountsConfigured(loadedSettings);
    if (needsApplyMode) {
      setApplyDialogOpen(true);
      return;
    }
    saveSettings();
  };

  const planSummaryLabel = useMemo(() => {
    const kindLabel = intl.formatMessage({
      id:
        form.membership_billing_kind === "yearly"
          ? "org.membership.plan.kindYearly"
          : "org.membership.plan.kindMonthly",
    });
    const amount = normalizeAmount(form.membership_amount);
    const symbol = membershipCurrencySymbol(currencyCode);
    if (amount === null) {
      return intl.formatMessage({ id: "org.membership.plan.summaryUnset" }, { kind: kindLabel });
    }
    const amountLabel = symbol ? `${symbol}${amount}` : String(amount);
    if (form.membership_billing_kind === "yearly" && form.membership_split_into_12) {
      return intl.formatMessage(
        { id: "org.membership.plan.summaryYearlySplit" },
        { kind: kindLabel, amount: amountLabel },
      );
    }
    return intl.formatMessage(
      { id: "org.membership.plan.summary" },
      { kind: kindLabel, amount: amountLabel },
    );
  }, [currencyCode, form.membership_amount, form.membership_billing_kind, form.membership_split_into_12, intl]);

  if (!selectedOrganization) {
    return null;
  }

  if (loading) return <CircularProgress />;

  const isYearly = form.membership_billing_kind === "yearly";
  const currencySymbol = membershipCurrencySymbol(currencyCode);

  return (
    <Box maxWidth={960}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <RevaText variant="h5">{intl.formatMessage({ id: "org.membership.settings.title" })}</RevaText>
        <Chip size="small" color="primary" variant="outlined" label={planSummaryLabel} />
      </Stack>

      <Stack spacing={3}>
        <Box>
          <RevaText variant="h6" sx={{ mb: 1.5 }}>
            {intl.formatMessage({ id: "org.membership.sections.plan" })}
          </RevaText>
          <Stack spacing={2} maxWidth={560}>
            <TextField
              select
              label={intl.formatMessage({ id: "org.membership.settings.enforcement" })}
              value={form.membership_enforcement}
              onChange={(e) => setForm({ ...form, membership_enforcement: e.target.value })}
            >
              <MenuItem value="soft">{intl.formatMessage({ id: "org.membership.settings.enforcementSoft" })}</MenuItem>
              <MenuItem value="hard">{intl.formatMessage({ id: "org.membership.settings.enforcementHard" })}</MenuItem>
            </TextField>
            <TextField
              type="number"
              label={intl.formatMessage({ id: "org.membership.settings.graceDays" })}
              value={form.membership_grace_days}
              onChange={(e) => setForm({ ...form, membership_grace_days: Number(e.target.value) })}
            />
            <TextField
              select
              label={intl.formatMessage({ id: "org.membership.settings.requireToPlay" })}
              value={form.require_membership_to_play ? "yes" : "no"}
              onChange={(e) => setForm({ ...form, require_membership_to_play: e.target.value === "yes" })}
            >
              <MenuItem value="no">{intl.formatMessage({ id: "org.membership.settings.no" })}</MenuItem>
              <MenuItem value="yes">{intl.formatMessage({ id: "org.membership.settings.yes" })}</MenuItem>
            </TextField>
            <TextField
              select
              label={intl.formatMessage({ id: "org.membership.settings.billingKind" })}
              value={form.membership_billing_kind}
              onChange={(e) =>
                setForm({
                  ...form,
                  membership_billing_kind: e.target.value,
                  membership_split_into_12: e.target.value === "yearly" ? form.membership_split_into_12 : false,
                })
              }
            >
              <MenuItem value="monthly">{intl.formatMessage({ id: "org.membership.plan.kindMonthly" })}</MenuItem>
              <MenuItem value="yearly">{intl.formatMessage({ id: "org.membership.plan.kindYearly" })}</MenuItem>
            </TextField>
            <TextField
              type="number"
              label={intl.formatMessage({
                id: isYearly ? "org.membership.settings.amountYearly" : "org.membership.settings.amountMonthly",
              })}
              value={form.membership_amount ?? ""}
              onChange={(e) => setForm({ ...form, membership_amount: e.target.value })}
              helperText={
                currencyCode
                  ? intl.formatMessage({ id: "org.membership.settings.currencyHint" }, { code: currencyCode })
                  : intl.formatMessage({ id: "org.membership.settings.currencyMissing" })
              }
              InputProps={{
                startAdornment: currencySymbol ? (
                  <InputAdornment position="start">{currencySymbol}</InputAdornment>
                ) : undefined,
              }}
            />
            {isYearly && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.membership_split_into_12)}
                    onChange={(e) => setForm({ ...form, membership_split_into_12: e.target.checked })}
                  />
                }
                label={intl.formatMessage({ id: "org.membership.settings.splitInto12" })}
              />
            )}
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {intl.formatMessage({ id: "org.membership.settings.save" })}
            </Button>
          </Stack>
        </Box>

        <OrganizationMembershipOverviewCards refreshKey={overviewRefreshKey} onEnrolled={refreshPayments} />
        <OrganizationMembershipImportSection
          amountsConfigured={amountsConfigured(loadedSettings)}
          onCommitted={refreshDashboard}
        />
        <OrganizationMembershipPaymentsList refreshKey={paymentsRefreshKey} currencyCode={currencyCode} />
      </Stack>

      <Dialog open={applyDialogOpen} onClose={() => !saving && setApplyDialogOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "org.membership.plan.applyTitle" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{intl.formatMessage({ id: "org.membership.plan.applyBody" })}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => setApplyDialogOpen(false)} disabled={saving}>
            {intl.formatMessage({ id: "org.membership.plan.applyCancel" })}
          </Button>
          <Button variant="outlined" onClick={() => saveSettings("next_period")} disabled={saving}>
            {intl.formatMessage({ id: "org.membership.plan.nextPeriod" })}
          </Button>
          <Button color="warning" variant="contained" onClick={() => saveSettings("rebuild_unpaid")} disabled={saving}>
            {intl.formatMessage({ id: "org.membership.plan.rebuildUnpaid" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationMembershipSettingsPage;
